import { browserHistory } from 'react-router'
import { includes, omit } from 'lodash'
import store from './store'
import { setSnackbarMessage } from './actions/snackbar'
import { setDialogMessage } from './actions/dialog'
import config from './config'
import auth from './auth'

const qs = require('qs')
const baseUrl: string = config.apiUrl

const { fetch } = window

const serialize = (obj: Record<string, unknown>): string => {
  return qs.stringify(obj)
}

export interface RequestOptions extends RequestInit {
  body?: any // eslint-disable-line @typescript-eslint/no-explicit-any
  acceptableStatuses?: number[]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const request = (path: string, options: RequestOptions): Promise<any> => {
  if (options && typeof options.body === 'object' && !(options.body instanceof window.FormData)) {
    options.body = JSON.stringify(options.body)
  }

  const token = auth.getToken()

  const { acceptableStatuses } = options

  const defaults: RequestInit & { headers: Record<string, string> } = {
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  }

  if (token) {
    defaults.headers['Authorization'] = `Bearer ${token}`
  }

  if (options.acceptableStatuses) {
    options = omit(options, 'acceptableStatuses') as RequestOptions
  }

  return fetch(baseUrl + path, Object.assign(defaults, options))
    .then((response: Response) => {
      if (response.status <= 401 || includes(acceptableStatuses, response.status)) {
        return response
      } else if (response.status === 403) {
        store.dispatch(setSnackbarMessage('Session expired'))
        require('./auth').default.logout()
        browserHistory.push('/login')
      } else if (response.status === 404) {
        store.dispatch(setDialogMessage('Not found', 'The requested resource could not be found'))
        return Promise.reject()
      } else if (response.status === 500) {
        response
          .json()
          .then((body: { hint?: string; detail?: string }) => {
            let message = body.hint
            if (!message && body.detail) {
              message = body.detail
            }
            store.dispatch(setDialogMessage(response.statusText, message || ''))
            return Promise.reject()
          })
        return Promise.reject()
      } else {
        store.dispatch(setDialogMessage(
          `Unknown error ${response.status}`,
          `The server has returned an unexpected error (${response.statusText}), please try it again later`
        ))
        return Promise.reject()
      }
    })
    .catch((err: Error) => {
      store.dispatch(setDialogMessage('Network error', err?.message || ''))
    })
}

const get = (path: string, options?: RequestOptions): Promise<any> => {
  let url = path

  if (options && typeof options.body === 'object') {
    const body = options.body
    delete options.body
    url = `${path}?${serialize(body)}`
  }

  return request(url, Object.assign({ method: 'GET' }, options))
}

const post = (path: string, options?: RequestOptions): Promise<any> =>
  request(path, Object.assign({ method: 'POST' }, options))

const put = (path: string, options?: RequestOptions): Promise<any> =>
  request(path, Object.assign({ method: 'PUT' }, options))

const patch = (path: string, options?: RequestOptions): Promise<any> =>
  request(path, Object.assign({ method: 'PATCH' }, options))

const del = (path: string, options?: RequestOptions): Promise<any> =>
  request(path, Object.assign({ method: 'DELETE' }, options))

const patchFile = (path: string, options?: RequestOptions): Promise<any> =>
  request(path, Object.assign({
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${auth.getToken()}`
    }
  }, options))

const postFile = (path: string, options?: RequestOptions): Promise<any> =>
  request(path, Object.assign({
    method: 'POST',
    headers: {
      Authorization: `Bearer ${auth.getToken()}`
    }
  }, options))

export default {
  get,
  post,
  put,
  patch,
  del,
  patchFile,
  postFile
}
